import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Icon, palette } from '@livable/saas-react-components';
import { Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { splitCentsByDays } from 'src/pages/NewBillingModule/ApplyBillingFull/MeterDetails';
import { toSplitRowShares } from 'src/pages/NewBillingModule/ApplyBillingFull/MeterDetails/splitRowDisplay';
import { formatMeterRate, formatMoney, toMeasure } from 'src/pages/NewBillingModule/ApplyBillingFull/MeterDetails/toSaveRows';

// Reads and usage print with at most four decimals, the ticket's rule. Meter details' grid uses the data
// grid's default number formatter, which stops at three, so a value such as 150.1234 prints in full here
// and as 150.123 there. Money and the rate print the way Meter details prints them.
const MEASURE_FORMAT = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 4
});
const formatMeasure = value => value == null || value === '' ? '' : MEASURE_FORMAT.format(toMeasure(Number(value)));
const idKey = value => String(value !== null && value !== void 0 ? value : '').toLowerCase();
const compareIds = (a, b) => {
  if (a === b) return 0;
  return a < b ? -1 : 1;
};
// Not exported by Meter details (byResidencyThenRow), so copied: the order decides which resident of a
// shared meter takes the odd cent, and it has to be the order Meter details uses. Meter details' own row
// mapping sets allocationId to the saved row's id, and the read returns that id as `id`, so `id` is the key
// here. Two rows of one meter on one residency therefore split the same way in both views.
const byResidencyThenRow = (a, b) => compareIds(idKey(a.residencyId), idKey(b.residencyId)) || compareIds(idKey(a.id), idKey(b.id));
const byUnitName = (a, b) => {
  var _a$unitName, _b$unitName;
  return String((_a$unitName = a.unitName) !== null && _a$unitName !== void 0 ? _a$unitName : '').localeCompare(String((_b$unitName = b.unitName) !== null && _b$unitName !== void 0 ? _b$unitName : ''), undefined, {
    numeric: true
  });
};
const cellSx = {
  whiteSpace: 'nowrap'
};
const numberCellSx = _objectSpread(_objectSpread({}, cellSx), {}, {
  textAlign: 'right'
});

/** The saved meter rows of one cycle, read only. Rows, money, rate and shared meter shares match Meter details. */
export const SubmeterMeterRows = _ref => {
  let rows = _ref.rows;
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const ordered = [...rows].sort(byUnitName);
  const shares = toSplitRowShares(ordered, {
    splitCents: splitCentsByDays,
    compareRows: byResidencyThenRow
  });
  const shownCharge = (row, field) => {
    var _shares$get$field, _shares$get;
    return formatMoney((_shares$get$field = (_shares$get = shares.get(row.id)) === null || _shares$get === void 0 ? void 0 : _shares$get[field]) !== null && _shares$get$field !== void 0 ? _shares$get$field : row[field]);
  };
  const columns = [{
    key: 'unit',
    label: t('allocations.submeter_rows.unit')
  }, {
    key: 'resident',
    label: t('allocations.submeter_rows.resident')
  }, {
    key: 'meter',
    label: t('allocations.submeter_rows.meter_id')
  }, {
    key: 'start',
    label: t('allocations.submeter_rows.start_read'),
    numeric: true
  }, {
    key: 'end',
    label: t('allocations.submeter_rows.end_read'),
    numeric: true
  }, {
    key: 'usage',
    label: t('allocations.submeter_rows.usage'),
    numeric: true
  }, {
    key: 'rate',
    label: t('allocations.submeter_rows.rate'),
    numeric: true
  }, {
    key: 'base',
    label: t('allocations.submeter_rows.base_charge'),
    numeric: true
  }, {
    key: 'usageCharge',
    label: t('allocations.submeter_rows.usage_charge'),
    numeric: true
  }, {
    key: 'total',
    label: t('allocations.submeter_rows.total_charge'),
    numeric: true
  }];
  if (!ordered.length) {
    return /*#__PURE__*/React.createElement(Typography, {
      color: "light",
      sx: {
        padding: '24px 32px'
      },
      variant: "body1"
    }, t('allocations.submeter_rows.empty'));
  }
  return /*#__PURE__*/React.createElement(TableContainer, {
    sx: {
      borderTop: "1px solid ".concat(palette.shades[300]),
      maxHeight: 480
    }
  }, /*#__PURE__*/React.createElement(Table, {
    size: "small",
    stickyHeader: true
  }, /*#__PURE__*/React.createElement(TableHead, null, /*#__PURE__*/React.createElement(TableRow, null, columns.map(column => /*#__PURE__*/React.createElement(TableCell, {
    key: column.key,
    sx: column.numeric ? numberCellSx : cellSx
  }, column.label)))), /*#__PURE__*/React.createElement(TableBody, null, ordered.map((row, index) => {
    var _row$id;
    return /*#__PURE__*/React.createElement(TableRow, {
      key: (_row$id = row.id) !== null && _row$id !== void 0 ? _row$id : "".concat(row.unitId, "-").concat(row.meterSerialNumber, "-").concat(index)
    }, /*#__PURE__*/React.createElement(TableCell, {
      sx: cellSx
    }, row.unitName), /*#__PURE__*/React.createElement(TableCell, {
      sx: cellSx
    }, row.residentFullName), /*#__PURE__*/React.createElement(TableCell, {
      sx: cellSx
    }, row.meterSerialNumber), /*#__PURE__*/React.createElement(TableCell, {
      sx: numberCellSx
    }, formatMeasure(row.startMeterRead)), /*#__PURE__*/React.createElement(TableCell, {
      sx: numberCellSx
    }, formatMeasure(row.endMeterRead)), /*#__PURE__*/React.createElement(TableCell, {
      sx: numberCellSx
    }, [formatMeasure(row.meterUsage), row.unitsOfMeasure].filter(Boolean).join(' ')), /*#__PURE__*/React.createElement(TableCell, {
      sx: numberCellSx
    }, formatMeterRate(row.meterRate)), /*#__PURE__*/React.createElement(TableCell, {
      sx: numberCellSx
    }, shownCharge(row, 'baseCharge')), /*#__PURE__*/React.createElement(TableCell, {
      sx: numberCellSx
    }, shownCharge(row, 'usageCharge')), /*#__PURE__*/React.createElement(TableCell, {
      sx: numberCellSx
    }, formatMoney(row.allocationCharge)));
  }))));
};
export const SubmeterMeterRowsError = () => {
  const _useTranslation2 = useTranslation(),
    t = _useTranslation2.t;
  return /*#__PURE__*/React.createElement("div", {
    role: "alert",
    style: {
      padding: '24px 32px'
    }
  }, /*#__PURE__*/React.createElement(Chip, {
    icon: /*#__PURE__*/React.createElement(Icon, {
      type: "warning"
    }),
    label: t('allocations.submeter_rows.error'),
    size: "large",
    variant: "warning"
  }));
};